/**
 * The Zunox tab's side of the engine service (`zunox-puller serve`, on
 * DELT4's own server): the commands staff send it and the results it sends
 * back, through the Hub.
 *
 * Every item is locked to the client's key (see `engineItems`), so the Hub
 * only sees each one's kind, a short label, its size and when it was made.
 * Commands carry a code only this member of staff and the engine can make;
 * results carry one only the engine (with the client's key) can make.
 *
 * Commands: `setup` (the client's settings, kick-off workbooks and readout
 * settings), `answers` (check-sheet answers typed in for someone),
 * `findings` (DELT4's kick-off workbook with its findings and changes filled
 * in), `request` (bring the results up to date, look at a day again, make
 * the final readout, release it). Results: `files` (the client's working files,
 * with the outcome of the last command), `day` (a morning's look at the day
 * that ended, with its check sheets), `measured` (the period so far),
 * `readout` (a draft or the final readout) and `problem`.
 */
import { EngineItemError, openReply, sealCommand } from '~/util/engineItems';
import { base64ToBytes, bytesToBase64 } from '~/util/bytes';
import { request } from '~/util/hubClient';
/** The client's own copy of DELT4's kick-off workbook, as the engine keeps
 * it: its Findings tab lists every readout step, ready to fill in. */
export const KICKOFF_COPY = 'workbooks/Zunox kick-off (DELT4).xlsx';
/** A system as people know it, not as the browser's address shows it: an SAP tenant's long address is "SAP
 * Datasphere", claude.ai is "Claude (in the browser)". Anything not known is shown as given, without "www.". */
export function systemName(name) {
    const n = (name || '').trim();
    const host = n.toLowerCase().replace(/^www\./, '');
    const known = [
        [/\.hcs\.cloud\.sap$/, 'SAP Datasphere'],
        [/(^|\.)sapanalytics\.cloud$/, 'SAP Analytics Cloud'],
        [/(^|\.)hana\.ondemand\.com$/, 'SAP BTP'],
        [/^claude\.ai$/, 'Claude (in the browser)'],
        [/^chatgpt\.com$/, 'ChatGPT'],
        [/^gemini\.google\.com$/, 'Gemini'],
        [/^docs\.google\.com$/, 'Google Docs and Sheets'],
        [/^drive\.google\.com$/, 'Google Drive'],
        [/^mail\.google\.com$/, 'Gmail'],
        [/^calendar\.google\.com$/, 'Google Calendar'],
        [/^mail\.zoho\.com(\.au)?$/, 'Zoho Mail'],
        [/^(app\.)?xero\.com$/, 'Xero'],
        [/^dashboard\.delt4\.ai$/, 'the Zunox dashboard'],
        [/^github\.com$/, 'GitHub'],
        [/(^|\.)linkedin\.com$/, 'LinkedIn'],
        [/(^|\.)atlassian\.net$/, 'Jira'],
        [/(^|\.)sharepoint\.com$/, 'SharePoint'],
    ];
    for (const [pattern, said] of known)
        if (pattern.test(host))
            return said;
    return n.replace(/^www\./i, '');
}
// ---------------------------------------------------------------- the Hub
function base(orgId) {
    return `/v1/admin/organizations/${orgId}/engine`;
}
/** A client's items of these kinds (all, when none are given), newest first. */
export function listItems(orgId, kinds = [], limit = 50, before = null) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (kinds.length)
        params.set('kinds', kinds.join(','));
    if (before)
        params.set('before', before);
    return request(`${base(orgId)}/items?${params}`);
}
export function getItem(orgId, id) {
    return request(`${base(orgId)}/items/${id}`);
}
/** Calls off a command the engine hasn't started yet. False when it already
 * had (the Hub answers 409). */
export async function callOff(orgId, id) {
    try {
        await request(`${base(orgId)}/items/${id}/state`, {
            method: 'POST',
            body: JSON.stringify({ state: 'failed' }),
        });
        return true;
    }
    catch (err) {
        if (err.status === 409)
            return false;
        throw err;
    }
}
export function listSheets(orgId, label) {
    const params = new URLSearchParams({ limit: '1000' });
    if (label)
        params.set('label', label);
    return request(`${base(orgId)}/sheets?${params}`);
}
/** How much later than the engine made a result the Hub may list it as
 * taking it: the engine sends each the moment it's made. Listed later than
 * that, it was put back in to look newer than it is. */
const RELISTED_MS = 60 * 60 * 1000;
/** An item, unlocked with the client's key and checked as the engine's own.
 * `expected` is what it was listed as: the item the Hub hands back must be
 * that one (its id, kind and label, which the engine signed), so a Hub can't
 * answer "the 1 October day" with the signed result for 15 September. */
export async function openItem(reader, id, expected) {
    const info = await getItem(reader.orgId, id);
    if (info.id !== id ||
        (expected && (info.kind !== expected.kind || info.label !== expected.label))) {
        throw new EngineItemError('The Hub answered with a different result from the one it listed. Tell DELT4 before relying on anything here.');
    }
    const { header, payload } = await openReply(reader.secret, reader.orgId, info, reader.engineSigningKey);
    const made = Date.parse(header.made_at);
    const listed = Date.parse(info.made_at);
    if (!(made > 0) || !(listed > 0) || listed - made > RELISTED_MS) {
        throw new EngineItemError(`The Hub lists this result as taken ${new Date(listed).toLocaleString()}, long after the engine made it (${new Date(made).toLocaleString()}): it was put back in to look newer than it is. Tell DELT4 before relying on anything here.`);
    }
    return { info, payload, madeAt: header.made_at };
}
/** The newest result of a kind (and label, when given), unlocked, or null
 * when there's none. The Hub orders its listing, so the newest it shows is
 * compared with the newest this browser has opened of the same: a Hub can
 * hold a result back, but can't pass an older one off as the newest without
 * it showing, in a browser that has seen the newer one. */
export async function newest(reader, kind, label) {
    const items = await listItems(reader.orgId, [kind], label === undefined ? 1 : 30);
    const item = label === undefined ? items[0] : items.find(i => i.label === label);
    if (!item)
        return null;
    // Held to the kind asked for (and the label, when one was), not to what
    // the listing says the item is: a Hub that lists a different kind's item
    // first can't have it read as this one.
    const opened = await openItem(reader, item.id, { kind, label: label ?? item.label });
    const key = `zunox:newest:${reader.orgId}:${kind}:${label ?? '*'}`;
    const seen = readNewest(key);
    if (seen && Date.parse(opened.madeAt) < Date.parse(seen)) {
        throw new EngineItemError(`The newest ${kind === 'files' ? 'set-up' : kind} result the Hub lists is from ${new Date(opened.madeAt).toLocaleString()}, but this browser has opened one from ${new Date(seen).toLocaleString()}: the Hub is holding the newer one back. Tell DELT4 before relying on anything here.`);
    }
    if (!seen || Date.parse(opened.madeAt) > Date.parse(seen))
        writeNewest(key, opened.madeAt);
    return opened;
}
function readNewest(key) {
    try {
        return localStorage.getItem(key);
    }
    catch {
        return null;
    }
}
function writeNewest(key, madeAt) {
    try {
        localStorage.setItem(key, madeAt);
    }
    catch {
        // No storage here: only the signatures protect this browser.
    }
}
/** The newest item of a kind, unlocked, or null when there's none yet. */
export function latest(reader, kind) {
    return newest(reader, kind);
}
/** Seals a command for the engine and leaves it on the Hub. Returns its id. */
export async function send(sender, kind, label, payload) {
    const envelope = await sealCommand({ ...sender, kind, label, payload: { v: 1, ...payload } });
    const created = await request(`${base(sender.orgId)}/items`, {
        method: 'POST',
        body: JSON.stringify({ kind, label, ...envelope }),
    });
    return created.id;
}
// ---------------------------------------------------------------- reading what came back
/** One of the client's files, as bytes. */
export function fileBytes(files, name) {
    return files[name] === undefined ? null : base64ToBytes(files[name]);
}
export function fileText(files, name) {
    const bytes = fileBytes(files, name);
    return bytes ? new TextDecoder().decode(bytes) : null;
}
/** The findings and changes in hand, or null when none have come yet. */
export function findingsIn(files) {
    const text = files ? fileText(files.files, 'findings.json') : null;
    if (!text)
        return null;
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
}
/** How many findings and changes are in, in words: "8 findings and 4
 * changes (Order-to-cash)". */
export function findingsWords(found) {
    const count = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
    const findings = Object.values(found.findings || {}).reduce((n, f) => n + Object.keys(f).length, 0);
    const changes = Object.values(found.changes || {}).reduce((n, c) => n + c.length, 0);
    const functions = Array.from(new Set([...Object.keys(found.findings || {}), ...Object.keys(found.changes || {})]));
    return `${count(findings, 'finding')} and ${count(changes, 'change')}${functions.length ? ` (${functions.join(', ')})` : ''}`;
}
/** Rows of a CSV file as objects, by its header (quoted fields allowed). */
export function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (quoted) {
            if (c === '"' && text[i + 1] === '"') {
                field += '"';
                i++;
            }
            else if (c === '"') {
                quoted = false;
            }
            else {
                field += c;
            }
        }
        else if (c === '"') {
            quoted = true;
        }
        else if (c === ',') {
            row.push(field);
            field = '';
        }
        else if (c === '\n' || c === '\r') {
            if (c === '\r' && text[i + 1] === '\n')
                i++;
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        }
        else {
            field += c;
        }
    }
    if (field !== '' || row.length) {
        row.push(field);
        rows.push(row);
    }
    const [header, ...rest] = rows.filter(r => r.some(cell => cell !== ''));
    if (!header)
        return [];
    const names = header.map(h => h.replace(/^\uFEFF/, ''));
    return rest.map(r => Object.fromEntries(names.map((name, i) => [name, r[i] ?? ''])));
}
/** What a sheet's person has done with it, in a word. */
export function sheetStatus(sheet, onHub, now = new Date()) {
    if (onHub?.answered_at)
        return { label: 'Answered', variant: 'success' };
    if (onHub && new Date(onHub.expires_at) <= now)
        return { label: 'Closed', variant: 'secondary' };
    if (sheet.emailed)
        return { label: 'Emailed', variant: 'info' };
    return { label: 'Send by hand', variant: 'warning' };
}
/** A command's progress, in words. */
export function stateWords(state) {
    switch (state) {
        case 'waiting':
            return 'Waiting for the engine';
        case 'working':
            return 'The engine is on it';
        case 'done':
            return 'Done';
        case 'failed':
            return "Didn't work";
        default:
            return '';
    }
}
/** A request's label says what was asked (the payload says the same, under
 * the engine's code; the label is only for listing). */
const REQUEST_WORDS = {
    update: 'Bring the results up to date',
    final: 'Make the final readout',
    release: 'Release the final readout',
};
/** What each command was, in words, from its kind and label. */
export function commandWords(info) {
    switch (info.kind) {
        case 'setup':
            return 'Settings and workbooks';
        case 'answers':
            if (info.label === 'corrections')
                return 'Corrections from the Mornings tab';
            return `Answers typed in${/^\d{4}-\d{2}-\d{2}$/.test(info.label) ? ` for ${dayWords(info.label)}` : ''}`;
        case 'findings':
            return 'Findings and changes';
        case 'request':
            return info.label ? REQUEST_WORDS[info.label] ?? `Request: ${info.label}` : 'Request';
        default:
            return info.kind;
    }
}
export function requestLabel(what, day) {
    return what === 'day' && day ? `day ${day}` : what;
}
/** The verification figure in one line: how much of the time was checked, and by whom. */
export function verificationWords(v) {
    if (!v || !v.active_h)
        return '';
    return (`Of ${v.active_h} hours of work: ${percent(v.checked_by_people_share)} checked by the people who did it, ` +
        `${percent(v.corrected_by_delt4_share)} confirmed or corrected by DELT4, ` +
        `${percent(v.rules_only_share)} named by the rules alone, ${percent(v.unexplained_share)} not yet explained.`);
}
export function percent(share) {
    return share === null || share === undefined ? '–' : `${Math.round(share * 100)}%`;
}
/** A day (2026-09-01) in words: Tuesday 1 September. */
export function dayWords(day) {
    const [y, m, d] = day.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.toLocaleDateString('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
    });
}
/** A file from a result, for the browser to save. */
export function saveFile(name, bytes) {
    const type = name.endsWith('.pdf')
        ? 'application/pdf'
        : name.endsWith('.xlsx')
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : name.endsWith('.html')
                ? 'text/html'
                : name.endsWith('.json')
                    ? 'application/json'
                    : 'application/octet-stream';
    const url = URL.createObjectURL(new Blob([bytes], { type }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name.split('/').pop() || name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}
/** A page from a result, opened in a new tab, run in a locked frame: its
 * own styles only, no scripts and nothing sent anywhere. */
export function openPage(name, bytes) {
    const html = new TextDecoder().decode(bytes);
    const shell = '<!doctype html><meta charset="utf-8"><title>' +
        name.replace(/[<>&]/g, '') +
        '</title><style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style>' +
        '<iframe sandbox="" referrerpolicy="no-referrer"></iframe>';
    const tab = window.open('', '_blank');
    if (!tab) {
        saveFile(name, bytes);
        return;
    }
    tab.document.write(shell);
    tab.document.close();
    const frame = tab.document.querySelector('iframe');
    // The sandbox stops scripts; the policy stops the page fetching anything
    // (an image or stylesheet from elsewhere would say which readout was
    // opened and when), so it truly sends nothing anywhere.
    const policy = '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data:; font-src data:">';
    if (frame)
        frame.srcdoc = policy + html;
}
function readBytes(file) {
    if (typeof file.arrayBuffer === 'function') {
        return file.arrayBuffer().then(buffer => new Uint8Array(buffer));
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
    });
}
/** Files picked in the browser, as the command carries them: name → base64.
 * A folder's files keep their place under it (`order-to-cash/readout.json`),
 * without the folder's own name. */
export async function filesFor(list, fromFolder = false) {
    const out = {};
    for (const file of list) {
        const relative = file.webkitRelativePath || '';
        const name = fromFolder && relative.includes('/') ? relative.split('/').slice(1).join('/') : file.name;
        out[name] = bytesToBase64(await readBytes(file));
    }
    return out;
}
/** The readout settings among a folder's files: group.json and each
 * function's readout.json, records.json, hours.csv and patterns.json. */
export function readoutSettings(files) {
    const kept = ['readout.json', 'records.json', 'hours.csv', 'patterns.json'];
    return Object.fromEntries(Object.entries(files).filter(([name]) => {
        const parts = name.split('/');
        return ((parts.length === 1 && name === 'group.json') ||
            (parts.length === 2 && kept.includes(parts[1])));
    }));
}
/** Which of the unnamed groups a rule's words would name: a word rule of the
 * kick-off workbook (Their screens) matches a title containing the words, in
 * any case. Only the shape is tried, never the example title: a group holds
 * every title of that shape, so words found in the example alone (an invoice
 * number, say) would claim time that isn't theirs. Numbers are out of the
 * shapes, so words with digits never match. */
export function wouldName(groups, words) {
    const needle = words.trim().toLowerCase();
    if (!needle)
        return { groups: [], hours: 0 };
    const hit = groups.filter(g => g.shape.toLowerCase().includes(needle));
    return { groups: hit, hours: Math.round(hit.reduce((t, g) => t + g.seconds, 0) / 360) / 10 };
}
/** The words a title rule starts from: the longest run of a title shape
 * with no number in it, trimmed of dashes and spaces ("Invoice INV-# - Xero"
 * gives "Invoice INV"; words with a number in them never match a shape). */
export function titleWords(shape) {
    const pieces = shape
        .split('#')
        .map(p => p.replace(/^[\s\-–|:·,.]+|[\s\-–|:·,.]+$/g, ''))
        .filter(Boolean);
    return pieces.sort((a, b) => b.length - a.length)[0] ?? '';
}
/** What a rule made from one row of the unnamed groups matches, to start
 * with: the site when one was recorded, otherwise the application when it
 * isn't a browser (a browser row carries ": " and its kind of page), and
 * otherwise words from the window title. */
export function ruleFor(g) {
    if (g.site)
        return { match: 'host', value: g.site };
    if (g.app && !g.app.includes(': '))
        return { match: 'app', value: g.app };
    return { match: 'title', value: titleWords(g.shape) };
}
/** Which of the unnamed groups a rule would name, as the engine matches: a
 * site and its subdomains, an application by name, or words in the title. */
export function ruleWouldName(groups, match, value) {
    const v = value
        .trim()
        .toLowerCase()
        .replace(/^\.+|\.+$/g, '');
    if (!v)
        return { groups: [], hours: 0 };
    const hit = groups.filter(g => {
        if (match === 'host') {
            const site = g.site.toLowerCase();
            return site === v || site.endsWith(`.${v}`);
        }
        if (match === 'app')
            return g.app.toLowerCase() === v;
        return g.shape.toLowerCase().includes(v);
    });
    return { groups: hit, hours: Math.round(hit.reduce((t, g) => t + g.seconds, 0) / 360) / 10 };
}
/** What the accuracy figure rests on, in words, as the engine's measured page says it: its 95% margin, the
 * half-hours (drawn at random) and hours behind it, and how many of the questions asked were answered. A question
 * nobody answered is shown as not answered, never counted against the rules. */
export function accuracyBasis(check, replies) {
    const bits = [];
    const hh = check.half_hours ?? 0;
    if (hh) {
        bits.push(`${hh} half-hour${hh === 1 ? '' : 's'}${check.drawn_at_random ? ' drawn at random' : ''} (${Math.round((check.active_h ?? 0) * 10) / 10} hours)`);
    }
    if (replies && replies.asked) {
        const tail = [
            replies.not_sure ? `${replies.not_sure} not sure` : '',
            replies.set_aside ? `${replies.set_aside} set aside` : '',
            replies.not_answered ? `${replies.not_answered} not answered` : '',
        ].filter(Boolean);
        bits.push(`${replies.answered} of ${replies.asked} questions answered${tail.length ? ` (${tail.join(', ')})` : ''}`);
    }
    const m = check.right_margin;
    return {
        margin: m === null || m === undefined ? '' : ` (give or take ${Math.round(100 * m)}%)`,
        basis: bits.join('; '),
    };
}
/** The hours resting on what the team said rather than on records, said beside what was measured. */
export function assumedWords(a) {
    if (!a || !a.hours_a_week)
        return '';
    const share = a.share === null || a.share === undefined ? 'some' : `${Math.round(100 * a.share)}%`;
    return `Besides these, ${a.hours_a_week} hours a week (${share} of the hours shown on the steps) are assumed: ${a.method}.`;
}
